import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import Overlay from '../Overlay';
import InlineMessage from '../InlineMessage';
import useMessage from '../../lib/useMessage';

// Issue #381, Session 78n6. The Remove control.
//
// One button, and Stede answers the question before the admin presses
// anything: it counts what the person is attached to and says which of two
// things is about to happen. An admin never picks between Delete and Archive
// and finds out afterwards which was safe — gate callout E.
//
// The counts are the design, not decoration (gate callout D). They are the
// evidence that the route Stede chose is the right one, and five rows of
// "None" is a far better argument than a sentence saying it is safe.

// The census returns one row per foreign key into `users` — 24 of them across
// 19 tables on production, generated from the schema so it cannot drift. That
// is the right level of detail for correctness and the wrong one for a person
// standing in front of a decision, so the rows are grouped for reading while
// the arithmetic stays exhaustive. Anything not named below still counts; it
// falls into "Other records", which exists precisely so a table added later
// shows up rather than vanishing from the total.
const GROUPS = [
  {
    label: 'Stock movements',
    match: ['inventory_transactions.user_id'],
  },
  {
    label: 'Requests raised or approved',
    match: [
      'material_requests.requested_by',
      'material_requests.approved_by',
      'material_requests.assigned_to',
      'material_requests.invoiced_by',
      'material_request_items.receipt_closed_by',
      'material_request_items.invoice_closed_by',
      'material_request_items.catalog_review_dismissed_by',
      'material_request_item_close_log.changed_by',
      'material_request_split_log.changed_by',
    ],
  },
  {
    label: 'Invoices entered',
    match: [
      'material_request_invoices.entered_by',
      'material_request_invoices.discrepancy_resolved_by',
      'material_request_invoice_resolution_log.changed_by',
    ],
  },
  {
    label: 'Changes to settings, catalog or vendors',
    match: [
      'company_settings_audit_log.changed_by',
      'taxonomy_change_log.changed_by',
      'inventory_vendor_audit_log.changed_by',
      'inventory_vendors.reviewed_by',
      'vendor_item_audit_log.changed_by',
      'vendor_items.locked_by',
      'user_permission_toggle_log.changed_by',
      'user_permission_toggle_log.target_user_id',
    ],
  },
  {
    label: 'Labels printed',
    match: ['print_jobs.requested_by'],
  },
];

// These cascade. They are not counted toward "attached" because they cannot
// block anything — but they ARE shown, because gate callout F asks route B to
// name what is destroyed rather than only what survives.
const CASCADES = ['notifications.user_id', 'user_notification_preferences.user_id'];

function keyOf(r) {
  return `${r.referencing_table}.${r.referencing_column}`;
}

export default function RemoveUserSheet({ user, isMobile, onClose, onDone }) {
  const [census, setCensus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [typed, setTyped] = useState('');
  const msg = useMessage();

  const call = useCallback(async (action, extra = {}) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) return { error: 'Your session has expired — please sign in again.' };

    const res = await fetch('/remove-user', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ user_id: user.id, action, ...extra }),
    });
    // #377's shape. Under `npm start` an unknown path returns index.html, so
    // res.json() throws on a page of HTML and the real cause reads as the app
    // being broken. Say which it is.
    const ct = res.headers.get('Content-Type') || '';
    if (!ct.includes('application/json')) {
      return { error: 'Removing people needs the deployed site — this service is not served by the local dev server.' };
    }
    const out = await res.json();
    if (!res.ok) return { error: out.error || 'Something went wrong.' };
    return out;
  }, [user.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const out = await call('census');
      if (cancelled) return;
      if (out.error) msg.showError(out.error);
      else setCensus(out);
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // `call` is memoised on the user's id, so this runs once per person the
    // sheet is opened for. `msg` is deliberately not a dependency — it is a
    // hook object whose identity changes every render, and depending on it
    // would re-run the census on every keystroke in the confirm box.
  }, [call]); // eslint-disable-line

  async function perform(action) {
    setWorking(true);
    msg.clearMessage();
    const out = await call(action, action === 'remove' ? { confirm_name: typed.trim() } : {});
    setWorking(false);
    if (out.error) { msg.showError(out.error); return; }
    onDone(out);
  }

  const firstName = (user.full_name || '').split(' ')[0] || 'them';
  const route = census?.route;
  const rows = census?.counts || [];

  const grouped = GROUPS.map(g => ({
    label: g.label,
    count: rows
      .filter(r => g.match.includes(keyOf(r)))
      .reduce((n, r) => n + Number(r.row_count), 0),
  }));

  // Anything the groups above do not name, so a table added later cannot go
  // missing from what the admin is shown.
  const namedKeys = new Set([...GROUPS.flatMap(g => g.match), ...CASCADES]);
  const otherCount = rows
    .filter(r => !namedKeys.has(keyOf(r)) && r.blocks_delete)
    .reduce((n, r) => n + Number(r.row_count), 0);
  if (otherCount > 0) grouped.push({ label: 'Other records', count: otherCount });

  const cascadeCount = rows
    .filter(r => CASCADES.includes(keyOf(r)))
    .reduce((n, r) => n + Number(r.row_count), 0);

  const nameMatches = typed.trim() === (user.full_name || '').trim();

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={working ? undefined : onClose} maxWidth={560}>
      <div className="modal-header">
        <span className="modal-title">
          {route === 'archived' ? `Restore ${firstName}` : `Remove ${user.full_name}`}
        </span>
        <button className="close-btn" onClick={onClose} disabled={working}>×</button>
      </div>

      <div className="modal-body">
        <InlineMessage type={msg.type} message={msg.message} onDismiss={msg.clearMessage} />

        <div className="text-muted" style={{ fontSize: 13, marginBottom: 14 }}>
          {user.email} · {(user.role || '').replace('_', ' ')}{user.branches?.name ? ` · ${user.branches.name}` : ''}
        </div>

        {loading ? (
          <div className="loading"><div className="spinner" />Counting what they are attached to...</div>
        ) : !census ? null : route === 'archived' ? (
          <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0 }}>
            <strong>{user.full_name}</strong> is archived. Restoring puts them back on the list as
            <strong> inactive</strong>, so they appear again without being handed working access —
            reactivate them separately if that is what you want.
          </p>
        ) : (
          <>
            <p style={{ fontSize: 14, lineHeight: 1.6, margin: '0 0 14px' }}>
              {route === 'delete' ? (
                <>Nothing is attached to this account. <strong>It can be deleted completely.</strong></>
              ) : (
                <>
                  <strong>{user.full_name}</strong> has <strong>{census.attached_total}</strong> records
                  attached. They will be <strong>archived rather than deleted</strong>, and their name
                  stays on all of them.
                </>
              )}
            </p>

            <div style={{ border: '1px solid var(--gray-ln, #E3E3DF)', borderRadius: 8, overflow: 'hidden' }}>
              {grouped.map(g => (
                <div
                  key={g.label}
                  style={{
                    display: 'flex', justifyContent: 'space-between', gap: 12,
                    padding: '9px 13px', fontSize: 13,
                    borderBottom: '1px solid var(--off-white, #F2F2EF)',
                  }}
                >
                  <span>{g.label}</span>
                  <span style={{ fontWeight: g.count ? 700 : 400 }} className={g.count ? '' : 'text-muted'}>
                    {g.count || 'None'}
                  </span>
                </div>
              ))}
              <div style={{
                display: 'flex', justifyContent: 'space-between', gap: 12,
                padding: '9px 13px', fontSize: 13,
              }}>
                <span>Has ever signed in</span>
                <span className={census.has_signed_in ? '' : 'text-muted'}
                      style={{ fontWeight: census.has_signed_in ? 700 : 400 }}>
                  {census.has_signed_in ? 'Yes' : 'No'}
                </span>
              </div>
            </div>

            {route === 'delete' ? (
              <>
                <div style={{
                  marginTop: 14, padding: '10px 12px', borderRadius: 6,
                  background: '#FDF3F3', border: '1px solid #F0CFCF', borderLeft: '3px solid #C34141',
                  fontSize: 13, color: '#7A2323', lineHeight: 1.5,
                }}>
                  {/* #822, Session 296 (the approved drawing, R2). This said the profile
                      and the sign-in "are both destroyed" — present tense — and Colby
                      read it exactly as written: already gone, so the greyed-out button
                      had nothing left to do. Nothing had been deleted. */}
                  <strong>Deleting cannot be undone:</strong> the profile and the sign-in will both be destroyed.
                </div>
                <div className="form-group" style={{ marginTop: 14 }}>
                  <label className="form-label">Type <strong>{user.full_name}</strong> to confirm</label>
                  <input
                    className="form-input"
                    value={typed}
                    onChange={e => setTyped(e.target.value)}
                    placeholder={user.full_name}
                    autoComplete="off"
                  />
                </div>
              </>
            ) : (
              <p className="text-muted" style={{ fontSize: 13, lineHeight: 1.55, margin: '13px 0 0' }}>
                {/* #381, amended at Session 296. This promised "any open session is
                    closed" — and none ever was: the call meant to close one goes to an
                    address Supabase does not have, so an open tab lasted until its
                    sign-in renewed. The app's own minute check (AuthContext.js) now
                    closes it, and the words say what happens. */}
                Archiving removes them from this list and <strong>ends their access</strong>: they cannot
                sign in again, and a tab they have open closes within a minute.
                {cascadeCount > 0 && <> Their {cascadeCount} notifications and alert preferences are deleted with them.</>}
                {' '}Everything above keeps their name.
              </p>
            )}
          </>
        )}
      </div>

      {/* Top-aligned so Cancel keeps its height when DELETE PERMANENTLY carries
          the line under it (#822); the shared footer stretches by default. */}
      <div className="modal-footer" style={{ alignItems: 'flex-start' }}>
        <button className="btn btn-ghost" onClick={onClose} disabled={working}>Cancel</button>
        {!loading && census && (
          route === 'archived' ? (
            <button className="btn btn-primary" onClick={() => perform('restore')} disabled={working}>
              {working ? 'Restoring...' : `Restore ${firstName}`}
            </button>
          ) : route === 'delete' ? (
            // Gate callout G, and the reason is recorded rather than assumed:
            // the button is BLOCKED rather than refusing after the click,
            // because an error banner renders hundreds of pixels above the
            // control that triggered it and reads as a broken button.
            //
            // #822, Session 296. Blocked, but never silent: while the typed name
            // does not match, the line under the button says what it waits for —
            // #492's standard, that a control refusing a press answers it.
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <button className="btn btn-danger" onClick={() => perform('remove')} disabled={working || !nameMatches}>
                {working ? 'Deleting...' : 'Delete permanently'}
              </button>
              {!working && !nameMatches && (
                <span className="text-muted" style={{ fontSize: 12 }}>Type the name above to delete.</span>
              )}
            </div>
          ) : (
            <button className="btn btn-primary" onClick={() => perform('remove')} disabled={working}>
              {working ? 'Archiving...' : `Archive ${firstName}`}
            </button>
          )
        )}
      </div>
    </Overlay>
  );
}
